/**
 * Copyright (c) 2019-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Loci } from 'molstar/lib/mol-model/loci';
import { Lines } from 'molstar/lib/mol-geo/geometry/lines/lines';
import { Text } from 'molstar/lib/mol-geo/geometry/text/text';
import { ParamDefinition as PD } from 'molstar/lib/mol-util/param-definition';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import { ShapeRepresentation } from 'molstar/lib/mol-repr/shape/representation';
import { Representation } from 'molstar/lib/mol-repr/representation';
import { Shape } from 'molstar/lib/mol-model/shape';
import { LinesBuilder } from 'molstar/lib/mol-geo/geometry/lines/lines-builder';
import { TextBuilder } from 'molstar/lib/mol-geo/geometry/text/text-builder';
import { Vec3 } from 'molstar/lib/mol-math/linear-algebra';
import { MarkerActions, MarkerAction } from 'molstar/lib/mol-util/marker-action';
import { distanceLabel } from 'molstar/lib/mol-theme/label';
import { LociLabelTextParams } from 'molstar/lib/mol-repr/shape/loci/common';
import { Sphere3D } from 'molstar/lib/mol-math/geometry';

const SharedParams = {
    unitLabel: PD.Text('\u212B', { isEssential: true })
};
const LineParams = {
    ...Lines.Params,
    ...SharedParams,
    lineSizeAttenuation: PD.Boolean(true),
    linesColor: PD.Color(ColorNames.lightgreen, { isEssential: true }),
    linesSize: PD.Numeric(0.075, { min: 0.01, max: 5, step: 0.01 }),
    dashLength: PD.Numeric(0.2, { min: 0.01, max: 0.2, step: 0.01 }),
};
const TextParams = {
    ...LociLabelTextParams,
    ...SharedParams,
};
const DistanceVisuals = {
    'lines': (ctx, getParams) => ShapeRepresentation(getLinesShape, Lines.Utils, { modifyState: s => ({ ...s, markerActions: MarkerActions.Highlighting }) }),
    'text': (ctx, getParams) => ShapeRepresentation(getTextShape, Text.Utils, { modifyState: s => ({ ...s, markerActions: MarkerAction.None }) }),
};
export const DistanceParams = {
    ...LineParams,
    ...TextParams,
    visuals: PD.MultiSelect(['lines', 'text'], PD.objectToOptions(DistanceVisuals)),
};
//
function getDistanceState() {
    return {
        sphereA: Sphere3D(),
        sphereB: Sphere3D(),
        center: Vec3(),
        distance: 0,
    };
}
function setDistanceState(pair, state) {
    const { sphereA, sphereB, center } = state;
    const [lociA, lociB] = pair.loci;
    Loci.getBoundingSphere(lociA, sphereA);
    Loci.getBoundingSphere(lociB, sphereB);
    Vec3.add(center, sphereA.center, sphereB.center);
    Vec3.scale(center, center, 0.5);
    state.distance = Vec3.distance(sphereA.center, sphereB.center);

    return state;
}

const tmpState = getDistanceState();

function getDistanceName(data, unitLabel) {
    return data.pairs.length === 1 ? `Distance ${distanceLabel(data.pairs[0], { unitLabel, measureOnly: true })}` : `${data.pairs.length} Distances`;
}
//
function buildLines(data, props, lines) {
    const builder = LinesBuilder.create(128, 64, lines);
    for (let i = 0, il = data.pairs.length; i < il; ++i) {
        setDistanceState(data.pairs[i], tmpState);
        builder.addFixedLengthDashes(tmpState.sphereA.center, tmpState.sphereB.center, props.dashLength, i);
    }
    return builder.getLines();
}
function getLinesShape(ctx, data, props, shape) {
    const lines = buildLines(data, props, shape && shape.geometry);
    const name = getDistanceName(data, props.unitLabel);
    const getLabel = (groupId) => distanceLabel(data.pairs[groupId], props); // CAMBIARE QUI PER VISUALIZZARE CUSTOM
    return Shape.create(name, data, lines, () => props.linesColor, () => props.linesSize, getLabel);
}
//
function buildText(data, props, text) {
    const builder = TextBuilder.create(props, 128, 64, text);
    for (let i = 0, il = data.pairs.length; i < il; ++i) {
        setDistanceState(data.pairs[i], tmpState);
        const { center, distance, sphereA, sphereB } = tmpState;
        const label = props.customText || `${distance.toFixed(2)} ${props.unitLabel}`;
        const radius = Math.max(2, sphereA.radius, sphereB.radius);
        const scale = radius / 2;
        builder.add(label, center[0], center[1], center[2], 1, scale, i);
    }
    return builder.getText();
}
function getTextShape(ctx, data, props, shape) {
    const text = buildText(data, props, shape && shape.geometry);
    const name = getDistanceName(data, props.unitLabel);
    const getLabel = (groupId) => distanceLabel(data.pairs[groupId], props);
    return Shape.create(name, data, text, () => props.textColor, () => props.textSize, getLabel);
}
export function DistanceRepresentationMod(ctx, getParams, label) {
    return Representation.createMulti(label, ctx, getParams, Representation.StateBuilder, DistanceVisuals);
}
